import {
  App,
  Button,
  Checkbox,
  Flex,
  Form,
  Input,
  Typography,
} from 'antd'
import { CheckboxChangeEvent } from 'antd/es/checkbox'
import { signIn } from 'aws-amplify/auth'
import Image from 'next/image'
import React, {
  useCallback,
  useEffect,
  useState,
} from 'react'

import { REMEMBER_MY_ACCOUNT } from '@/constants/local-storages'
import { useAppDispatch } from '@/redux/utils'
import { init } from '@/utils/init'

import ConfirmPasswordModal from './confirm-password-modal'
import ForgetPasswordBlock from './forget-password'
import LoginHeroImage from './login-hero-image.png'
import s from './login.module.css'

const { Text } = Typography
const { Password } = Input
const { Item } = Form
const { useApp } = App

interface FormValues {
  username: string;
  password: string;
  isStaging: boolean
}

export default function Login() {
  const dispatch = useAppDispatch()
  const [loading, setLoading] = useState(false)
  const [confirmPasswordModalOpen, setConfirmPasswordModalOpen] = useState(false)
  const { message } = useApp()
  const [fpOverlayOpened, setFpOverlayOpened] = useState(false)
  const [rememberMe, setRememberMe] = useState(false)
  const [form] = Form.useForm<FormValues>()

  useEffect(() => {
    const rememberedAccount = localStorage.getItem(REMEMBER_MY_ACCOUNT)
    setRememberMe(!!rememberedAccount)
    if (rememberedAccount) {
      form.setFieldValue('username', rememberedAccount)
    }
  }, [form])

  const onFinish = useCallback(
    async (values: FormValues) => {
      setLoading(true)
      try {
        const {
          isSignedIn,
          nextStep: { signInStep },
        } = await signIn(values)

        if (signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
          setConfirmPasswordModalOpen(true)
          setLoading(false)
          return
        }

        if (!isSignedIn || signInStep !== 'DONE') {
          void message.error('Login failed')
          setLoading(false)
          return
        }

        if (rememberMe) {
          localStorage.setItem(REMEMBER_MY_ACCOUNT, values.username)
        } else {
          localStorage.removeItem(REMEMBER_MY_ACCOUNT)
        }

        await init(dispatch)
      } catch (error) {
        if (error instanceof Error) {
          void message.error(error.message)
        } else {
          console.log(error)
        }
      }
      setLoading(false)
    },
    [rememberMe, dispatch, message],
  )

  return (
    <Flex vertical className={s.Root} justify="center" align="center">
      <Flex align="center">
        <Image width={773} src={LoginHeroImage} alt="Talk2Fans" />
        <div className={s.Content}>
          {fpOverlayOpened ? (
            <ForgetPasswordBlock onClose={() => setFpOverlayOpened(false)} />
          ) : (
            <Form<FormValues>
              form={form}
              layout="vertical"
              onFinish={onFinish}
            >
              <div className={s.Title}>Welcome back</div>
              <div className={s.SubTitle}>Manage your Bae with Talk2Fans</div>
              <Item name="username" label="Email">
                <Input size="large" required />
              </Item>
              <Item name="password" label="Password">
                <Password size="large" required />
              </Item>
              <Flex justify="space-between" align="center" style={{ marginBottom: 16 }}>
                <Checkbox
                  checked={rememberMe}
                  onChange={(e: CheckboxChangeEvent) => setRememberMe(e.target.checked)}
                >
                  Remember me
                </Checkbox>
                <a onClick={() => setFpOverlayOpened(true)}>Forget password?</a>
              </Flex>
              <Flex gap={8} align="center">
                <Button
                  size="large"
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  block
                >
                  Log in
                </Button>
              </Flex>
            </Form>
          )}
          <div style={{ marginTop: 16, textAlign: 'center' }}>
            <Text type="secondary">
              Version: {process.env.NEXT_PUBLIC_APP_VERSION}
            </Text>
          </div>
        </div>
      </Flex>
      <ConfirmPasswordModal
        open={confirmPasswordModalOpen}
        setOpen={setConfirmPasswordModalOpen}
      />
    </Flex>
  )
}
